/*
 * Zoning (heatmap) operations for the Demo Environment Management page.
 * Datasource-level entity. Mirrors sales-demo-env-setup's `src/lib/heatmap.js`,
 * adapted to use `CsqApiClient`. See `./templates/zoningTemplates.js` for the
 * default create-zoning payload.
 */

import { buildSegment } from './templates/zoningTemplates';

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function requestScreenshotSuggestions(client, datasourceId, { pageId, device, from, to, count, segmentId }) {
  return client.post(`/v3/projects/${datasourceId}/heatmaps/screenshots/suggestions`, {
    from,
    to,
    device,
    pageId,
    segment: buildSegment(segmentId),
    count,
  });
}

function getScreenshot(client, datasourceId, screenshotId) {
  return client.get(`/v3/projects/${datasourceId}/heatmaps/screenshots/${screenshotId}`);
}

/**
 * Polls the candidate screenshots (in suggestion order) until one is ready
 * (status "done" and exposes a snapshotId).
 */
async function waitForReadyScreenshot(client, datasourceId, screenshotIds, { attempts, delayMs, log }) {
  for (let attempt = 1; attempt <= attempts; attempt++) {
    for (const screenshotId of screenshotIds) {
      const screenshot = await getScreenshot(client, datasourceId, screenshotId);
      const status = (screenshot && screenshot.status) || 'unknown';
      const snapshotId = screenshot && screenshot.snapshotId;
      log(`    screenshot ${screenshotId}: status=${status} snapshotId=${snapshotId || 'none'}`);
      const ready = screenshot && screenshot.snapshotId && (!screenshot.status || screenshot.status === 'done');
      if (ready) {
        return { screenshotId: screenshot.screenshotId || screenshotId, snapshotId: screenshot.snapshotId };
      }
    }
    log(`  waiting for a screenshot to be ready (${attempt}/${attempts})...`);
    if (attempt < attempts) await sleep(delayMs);
  }
  throw new Error('No screenshot became ready within the allotted time.');
}

function markScreenshotPermanent(client, datasourceId, screenshotId) {
  return client.patch(`/v3/projects/${datasourceId}/heatmaps/screenshots`, {
    screenshots: [{ id: screenshotId, temporary: false }],
  });
}

function postZoning(client, datasourceId, { name, device, pageId, width, height, userAgent }) {
  return client.post(`/api/zonings-composite/v1/projects/${datasourceId}/zonings`, {
    name,
    type: 'v2',
    device,
    pageId,
    width,
    height,
    userAgent,
  });
}

function linkSnapshotToZoning(client, datasourceId, snapshotId, zoningId) {
  return client.post(`/v3/projects/${datasourceId}/snapshots/${snapshotId}/zonings/${zoningId}`);
}

function confirmZoningScreenshot(client, datasourceId, screenshotId) {
  return client.patch(`/api/zonings-composite/v1/projects/${datasourceId}/heatmaps/screenshots`, {
    screenshots: [{ id: screenshotId, temporary: false }],
  });
}

function finalizeSnapshotScreenshot(client, datasourceId, snapshotId) {
  return client.post(`/v3/projects/${datasourceId}/heatmaps/screenshots/${snapshotId}`);
}

/**
 * Confirms saving the zoning: flips `temporary` to false so the heatmap is
 * persisted and shown in the app.
 */
function persistZoning(client, datasourceId, zoningId, name) {
  return client.put(`/v1/projects/${datasourceId}/zonings/${zoningId}`, {
    name,
    temporary: false,
    projectId: Number(datasourceId),
  });
}

/**
 * Runs the full zoning (heatmap) creation flow for an existing page.
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @param {object} config - see `defaultZoningConfig` (must include a resolved `pageId`).
 * @param {(message: string) => void} [config.log]
 * @returns {Promise<{ zoning: object, snapshotId: number, screenshotId: number }>}
 */
export async function createZoning(client, datasourceId, config) {
  const log = config.log || (() => {});
  const { pageId, name, device, width, height, userAgent, from, to, count, segmentId, pollAttempts, pollDelayMs } = config;

  if (!pageId) {
    throw new Error('createZoning requires a resolved pageId (see resolvePage/resolvePageByUrl in csqMappings.js).');
  }

  log('Requesting screenshot suggestions...');
  const suggestions = await requestScreenshotSuggestions(client, datasourceId, { pageId, device, from, to, count, segmentId });
  const screenshotIds = (suggestions && suggestions.screenshotIds) || [];
  if (screenshotIds.length === 0) {
    throw new Error(
      `No screenshot suggestions returned for page ${pageId} (device ${device}, ${from} -> ${to}). ` +
        `The page may not have enough traffic in the selected date range, or the pageId/device is wrong. ` +
        `Raw response: ${JSON.stringify(suggestions)}`,
    );
  }
  log(`  got ${screenshotIds.length} candidate screenshot(s): ${screenshotIds.join(', ')}`);

  log('Waiting for a screenshot snapshot to be ready...');
  const { screenshotId, snapshotId } = await waitForReadyScreenshot(client, datasourceId, screenshotIds, {
    attempts: pollAttempts,
    delayMs: pollDelayMs,
    log,
  });
  log(`  ready: screenshot ${screenshotId} -> snapshot ${snapshotId}.`);

  log('Marking screenshot as permanent...');
  await markScreenshotPermanent(client, datasourceId, screenshotId);

  log('Creating zoning (heatmap)...');
  const zoning = await postZoning(client, datasourceId, { name, device, pageId, width, height, userAgent });
  log(`  created zoning ${zoning.id}.`);

  log('Attaching snapshot to zoning...');
  await linkSnapshotToZoning(client, datasourceId, snapshotId, zoning.id);

  log('Confirming zoning screenshot...');
  await confirmZoningScreenshot(client, datasourceId, screenshotId);

  log('Finalizing snapshot screenshot...');
  await finalizeSnapshotScreenshot(client, datasourceId, snapshotId);

  log('Confirming save (persisting zoning)...');
  const persisted = await persistZoning(client, datasourceId, zoning.id, name);
  log(`  zoning ${zoning.id} persisted (temporary=${persisted && persisted.temporary}).`);

  return { zoning, snapshotId, screenshotId };
}

/**
 * Lists all zonings (heatmaps) for a datasource.
 */
export async function listZonings(client, datasourceId) {
  const payload = await client.get(`/v1/projects/${datasourceId}/zonings`);
  const zonings = Array.isArray(payload) ? payload : (payload && (payload.items || payload.zonings)) || [];
  return zonings.map((zoning) => ({ id: zoning.id, name: zoning.name }));
}

/**
 * Lists all zonings (heatmaps) for a datasource with their full creation config
 * (device/pageId/width/height/userAgent), instead of just `{id, name}`. Used to
 * recreate a datasource's heatmaps on another datasource (see "Create from
 * Source" in entities-tab.js) — `pageId` still needs resolving to a page NAME
 * (via `listPages` in csqMappings.js) since page ids differ across datasources.
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @returns {Promise<Array<{ id: number, name: string, device: number, pageId: number, width: number, height: number, userAgent: string }>>}
 */
export async function listZoningsWithDetails(client, datasourceId) {
  const payload = await client.get(`/v1/projects/${datasourceId}/zonings`);
  const zonings = Array.isArray(payload) ? payload : (payload && (payload.items || payload.zonings)) || [];
  return zonings.map((zoning) => ({
    id: zoning.id,
    name: zoning.name,
    device: zoning.device,
    pageId: zoning.pageId ?? zoning.page?.id,
    width: zoning.width,
    height: zoning.height,
    userAgent: zoning.userAgent,
  }));
}

/**
 * Deletes a single zoning (heatmap).
 */
export async function deleteZoning(client, datasourceId, zoningId) {
  return client.del(`/v1/projects/${datasourceId}/zonings/${zoningId}`);
}
